import { useLoggedUser } from 'state/user';
import * as actions from 'services/authorization/actions';
import {
  ActionInput,
  PlaceholderActionInput,
  SurfaceNodePermissionCheck,
  UnLinkActionInput,
} from 'services/authorization/actions';

import {
  SubSurfaceNode,
  SurfaceNodePointsTo,
  SurfaceNodeType,
  SurfaceNodeUnion,
} from 'graphql/types';

type SurfaceAction = (surfaceNode?: SubSurfaceNode) => boolean;
type SurfaceNodeAction = (surfaceNode?: SurfaceNodeUnion) => boolean;

type UnLinkAction = (link: SurfaceNodePointsTo) => boolean;

export interface AuthorizationActions {
  canReadSurfaceNode: SurfaceAction;
  canDeleteSurfaceNode: SurfaceAction;
  canDeleteOwnedSurfaceNode: SurfaceNodeAction;
  canUpdateSurfaceNode: SurfaceNodeAction;
  canCreateSurfaceNode: SurfaceAction;
  canLinkSurfaceNode: SurfaceAction;
  canUnLinkSurfaceNode: UnLinkAction;
  canCopySurfaceNode: SurfaceAction;
  canCutSurfaceNode: SurfaceNodeAction;
  canPasteSurfaceNode: SurfaceAction;
  canRepositionSurfaceNode: SurfaceNodeAction;
  canAddToPlaceholderNode: SurfaceAction;
  canRemoveFromPlaceholderNode: SurfaceAction;
  canManageCollaboratorsOnSurfaceNode: SurfaceAction;
  canShareSurfaceNode: SurfaceAction;
  isNodeContextMenuVisible(surfaceNode?: SurfaceNodeUnion): boolean;
}

export default function useAuthorization(
  surface?: SubSurfaceNode,
): AuthorizationActions {
  const [loggedUser] = useLoggedUser();

  function surfaceUserResolver<Input>(
    action: SurfaceNodePermissionCheck<Input>,
  ): SurfaceAction {
    return (surfaceNode?: SurfaceNodeUnion) => {
      if (surface || surfaceNode) {
        const user = loggedUser?.id as string;

        let surfaceToCheck = surface;

        if (surfaceNode?.__typename) {
          surfaceToCheck = (surfaceNode.__typename === 'SubSurfaceNode'
            ? surfaceNode
            : surfaceNode?.surface) as SubSurfaceNode;
        }

        let role = surfaceToCheck?.loggedUserRole;

        //@ts-ignore
        return action({
          surfaceNode: surfaceToCheck,
          user,
          role,
        });
      }

      return false;
    };
  }

  function surfaceNodeUserResolver<Input>(
    action: SurfaceNodePermissionCheck<Input>,
  ): SurfaceNodeAction {
    return (surfaceNode?: SurfaceNodeUnion) => {
      if (surface || surfaceNode) {
        const user = loggedUser?.id as string;

        let surfaceToCheck = (surfaceNode ?? surface) as any;

        // @ts-ignore
        return action({
          surfaceNode: surfaceToCheck,
          user,
          role:
            surfaceToCheck?.type === SurfaceNodeType.SubSurface
              ? surfaceToCheck?.loggedUserRole
              : surface?.loggedUserRole,
        });
      }

      return false;
    };
  }

  function surfaceNodeUnLinkUserResolver(
    action: SurfaceNodePermissionCheck<UnLinkActionInput>,
  ): UnLinkAction {
    return (link: SurfaceNodePointsTo) => {
      if (surface) {
        const user = loggedUser?.id as string;
        //@ts-ignore
        return action({
          link,
          user,
          role: surface?.loggedUserRole,
        });
      }
      return false;
    };
  }

  function canDeleteAndCanDeleteOwnedPermissionCheck(
    surfaceNode?: SurfaceNodeUnion,
  ): boolean {
    const canDelete = surfaceUserResolver(actions.canDeleteSurfaceNode)();

    const canDeleteOwned = surfaceNodeUserResolver(
      actions.canDeleteOwnedSurfaceNode,
    )(surfaceNode);

    return canDelete || canDeleteOwned;
  }

  const authorizationAction = {
    canReadSurfaceNode: surfaceUserResolver<ActionInput>(
      actions.canReadSurfaceNode,
    ),
    canShareSurfaceNode: surfaceUserResolver<ActionInput>(
      actions.canShareSurfaceNode,
    ),
    canRepositionSurfaceNode: surfaceNodeUserResolver<ActionInput>(
      actions.canRepositionSurfaceNode,
    ),
    canManageCollaboratorsOnSurfaceNode: surfaceUserResolver<ActionInput>(
      actions.canManageCollaboratorsOnSurfaceNode,
    ),
    canUnLinkSurfaceNode: surfaceNodeUnLinkUserResolver(
      actions.canUnLinkSurfaceNode,
    ),
    canRemoveFromPlaceholderNode: surfaceUserResolver<PlaceholderActionInput>(
      actions.canRemoveFromPlaceholderNode,
    ),
    canAddToPlaceholderNode: surfaceUserResolver<PlaceholderActionInput>(
      actions.canAddToPlaceholderNode,
    ),
    canPasteSurfaceNode: surfaceUserResolver<ActionInput>(
      actions.canPasteSurfaceNode,
    ),
    canCutSurfaceNode: canDeleteAndCanDeleteOwnedPermissionCheck,
    canCopySurfaceNode: surfaceUserResolver<ActionInput>(
      actions.canCopySurfaceNode,
    ),
    canCreateSurfaceNode: surfaceUserResolver<ActionInput>(
      actions.canCreateSurfaceNode,
    ),
    canDeleteSurfaceNode: surfaceUserResolver<ActionInput>(
      actions.canDeleteSurfaceNode,
    ),
    canUpdateSurfaceNode: surfaceNodeUserResolver<ActionInput>(
      actions.canUpdateSurfaceNode,
    ),
    canDeleteOwnedSurfaceNode: surfaceNodeUserResolver<ActionInput>(
      actions.canDeleteOwnedSurfaceNode,
    ),
    canLinkSurfaceNode: surfaceUserResolver<ActionInput>(
      actions.canLinkSurfaceNode,
    ),
  };

  // create function for isNodeContextMenuVisible and allow to pass surfaceNode then check if user can
  // Add check for canDelete if surfaceNode is passed check for delete owned

  function isNodeContextMenuVisible(surfaceNode?: SurfaceNodeUnion) {
    const canDelete = canDeleteAndCanDeleteOwnedPermissionCheck(surfaceNode);

    return (
      authorizationAction.canLinkSurfaceNode() ||
      authorizationAction.canUpdateSurfaceNode(surfaceNode) ||
      authorizationAction.canCutSurfaceNode(surfaceNode) ||
      canDelete ||
      authorizationAction.canCopySurfaceNode()
    );
  }

  return {
    ...authorizationAction,
    isNodeContextMenuVisible,
  };
}
